import { Button, Modal, Table } from 'antd'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import { priceChangeHistoryApi } from '../../../../../apis'
import { getDateFormat } from '../../../../../common'
import { NumberFormat } from '../../../../../components'
import { translate } from '../../../../../languages'
import { PriceChangeHistoryEntity, TankEntity } from '../../../../../models'
import { RootState } from '../../../../../stores'
import { loadProfile } from '../../../../../stores/actions'

type PriceChangeHistoryModalComponentProps = {
  onClose: () => void
  tank: TankEntity
}

const pageSize = 15

export const PriceChangeHistoryModalComponent: React.FC<PriceChangeHistoryModalComponentProps> =
  ({ onClose, tank }) => {
    const dispatch = useDispatch()
    const { language } = useSelector((state: RootState) => state.language)
    const isSmallScreen = useMediaQuery({ maxWidth: '900px' })
    const isMediumScreen = useMediaQuery({
      maxWidth: '1299px',
      minWidth: '900px',
    })
    const isFullScreen = useMediaQuery({ minWidth: '1600px' })
    const [loading, setLoading] = React.useState(true)
    const [priceChangeHistories, setPriceChangeHistories] = React.useState<
      PriceChangeHistoryEntity[]
    >([])

    React.useEffect(() => {
      const load = async () => {
        try {
          setLoading(true)
          const resp = await priceChangeHistoryApi.getPriceChangeHistory(
            tank.id
          )
          setPriceChangeHistories(resp.data.data)
        } catch (e) {
          if (e.response && e.response.status && e.response.status === 401) {
            dispatch(loadProfile())
            setLoading(true)
            const resp = await priceChangeHistoryApi.getPriceChangeHistory(
              tank.id
            )
            setPriceChangeHistories(resp.data.data)
          }
        } finally {
          setLoading(false)
        }
      }
      load()
      // eslint-disable-next-line
    }, [])

    const columns = [
      {
        dataIndex: 'id',
        title: <span>{translate('common.id', language)}</span>,
        width: 30,
      },
      {
        title: (
          <span>{translate('tank.priceChangeHistory.time', language)}</span>
        ),
        render: (_: any, priceChangeHistory: PriceChangeHistoryEntity) =>
          getDateFormat(priceChangeHistory.time, true),
        width: 100,
      },
      {
        dataIndex: 'oldPrice',
        title: (
          <span>{translate('tank.priceChangeHistory.oldPrice', language)}</span>
        ),
        render: (_: any, pch: PriceChangeHistoryEntity) => (
          <NumberFormat value={pch.oldPrice} suffixKey="common.moneyType" />
        ),
        width: 80,
      },
      {
        dataIndex: 'newPrice',
        title: (
          <span>{translate('tank.priceChangeHistory.newPrice', language)}</span>
        ),
        render: (_: any, pch: PriceChangeHistoryEntity) => (
          <NumberFormat value={pch.newPrice} suffixKey="common.moneyType" />
        ),
        width: 80,
      },
      {
        title: (
          <span>
            {translate('tank.priceChangeHistory.difference', language)}
          </span>
        ),
        render: (_: any, priceChangeHistory: PriceChangeHistoryEntity) => (
          <NumberFormat
            value={Math.abs(
              priceChangeHistory.newPrice - priceChangeHistory.oldPrice
            )}
            suffixKey="common.moneyType"
          />
        ),
        width: 80,
      },
      {
        title: (
          <span>{translate('tank.priceChangeHistory.editor', language)}</span>
        ),
        render: (_: any, priceChangeHistory: PriceChangeHistoryEntity) =>
          priceChangeHistory.editor.name,
        width: 100,
      },
    ]

    return (
      <Modal
        title={`${translate('tank.priceChangeHistory.title', language)} (${
          tank.name
        })`}
        visible={true}
        onCancel={onClose}
        closable={true}
        footer={false}
        style={{ top: 20 }}
        width={isSmallScreen ? '85%' : isMediumScreen ? '75%' : '55%'}
      >
        <Table
          bordered
          pagination={
            priceChangeHistories.length > pageSize
              ? {
                  position: ['bottomCenter'],
                  showSizeChanger: false,
                  pageSize,
                }
              : false
          }
          loading={loading}
          columns={columns}
          dataSource={priceChangeHistories.map((item: any, index: number) => ({
            ...item,
            key: index,
          }))}
          size="small"
          style={{ paddingBottom: '40px' }}
          scroll={{ x: isFullScreen ? '0vw' : '95vw' }}
        />
        <div style={{ float: 'right', marginTop: '-20px' }}>
          <Button type="ghost" onClick={onClose}>
            {translate('common.close', language)}
          </Button>
        </div>
      </Modal>
    )
  }
